import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Search, Trash2, X, Loader2, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useModule } from "@/context/ModuleContext";

export default function Projects() {
  const { t, i18n } = useTranslation();
  const { module, meta } = useModule();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showNew, setShowNew] = useState(params.get("new") === "1");
  const [form, setForm] = useState({ name: "", client: "", location: "Florida", description: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = projects.filter((p) => {
    // Module filter: show projects matching current module, plus any "all"
    if (module && p.module && p.module !== "all" && p.module !== module) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      p.name?.toLowerCase().includes(q) ||
      p.client?.toLowerCase().includes(q) ||
      p.location?.toLowerCase().includes(q)
    );
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/projects", { ...form, module });
      setShowNew(false);
      setForm({ name: "", client: "", location: "Florida", description: "" });
      params.delete("new");
      setParams(params);
      navigate(`/projects/${data.id}/analyzer`);
    } finally {
      setSubmitting(false);
    }
  };

  const removeProject = async (id) => {
    if (!window.confirm(t("proj.confirm_delete"))) return;
    await api.delete(`/projects/${id}`);
    load();
  };

  return (
    <div className="p-6" data-testid="projects-page">
      <header className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">IQES · IQ-APP</div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mt-1">{t("proj.title")}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/projects/new"
            data-testid="open-wizard-btn"
            className="bg-white text-[#003366] border border-[#003366] hover:bg-[#003366]/5 rounded-xl px-4 py-2 font-medium text-sm transition-colors inline-flex items-center gap-2"
          >
            <Sparkles size={14} /> {t("proj.wizard") || (i18n.language?.startsWith("es") ? "Modo Guiado" : "Guided Mode")}
          </Link>
          <button
            data-testid="open-new-project-btn"
            onClick={() => setShowNew(true)}
            className="bg-[#003366] text-white hover:bg-[#002244] rounded-xl px-4 py-2 font-medium text-sm transition-colors inline-flex items-center gap-2"
          >
            <Plus size={14} /> {t("proj.new")}
          </button>
        </div>
      </header>

      <div className="mb-4 relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          data-testid="search-projects-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("proj.search")}
          className="w-full pl-9 pr-3 py-2 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#003366]/30 focus:border-[#003366] font-mono"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500 font-mono text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">{t("dash.no_projects")}</div>
        ) : (
          <table className="w-full" data-testid="projects-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs font-mono uppercase tracking-wider text-slate-500">
                <th className="text-left p-3">{t("proj.name")}</th>
                <th className="text-left p-3">{t("proj.client")}</th>
                <th className="text-left p-3">{t("proj.location")}</th>
                <th className="text-left p-3">{t("proj.status")}</th>
                <th className="text-left p-3">{t("proj.updated")}</th>
                <th className="p-3 w-32" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} data-testid={`project-row-${p.id}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="p-3 text-sm font-medium text-slate-900">
                    <Link to={`/projects/${p.id}/analyzer`} className="hover:text-[#003366] transition-colors">{p.name}</Link>
                  </td>
                  <td className="p-3 text-sm font-mono text-slate-700">{p.client || "—"}</td>
                  <td className="p-3 text-sm font-mono text-slate-700">{p.location}</td>
                  <td className="p-3">
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-xl border ${
                      p.status === "completed" ? "bg-green-50 text-green-800 border-green-200"
                      : p.status === "in_progress" ? "bg-[#F59E0B]/20 text-amber-900 border-[#F59E0B]"
                      : "bg-slate-100 text-slate-700 border-slate-200"}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="p-3 text-xs font-mono text-slate-600">{new Date(p.updated_at).toLocaleDateString()}</td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-2">
                      <Link to={`/projects/${p.id}/analyzer`} data-testid={`open-${p.id}`} className="text-xs font-mono uppercase tracking-wider text-[#003366] hover:underline px-2 py-1">
                        Analyzer
                      </Link>
                      <Link to={`/projects/${p.id}`} data-testid={`open-advanced-${p.id}`} className="text-xs font-mono uppercase tracking-wider text-slate-500 hover:underline px-2 py-1">
                        {i18n.language?.startsWith("es") ? "Avanzado" : "Advanced"}
                      </Link>
                      <button
                        data-testid={`delete-${p.id}`}
                        onClick={() => removeProject(p.id)}
                        className="text-slate-400 hover:text-red-600 p-1 transition-colors"
                        title={t("proj.delete")}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* New Project Modal */}
      {showNew && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="new-project-modal">
          <div className="bg-white rounded-xl border border-slate-200 w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">{t("proj.new")}</h3>
              <button
                data-testid="close-new-project-btn"
                onClick={() => { setShowNew(false); params.delete("new"); setParams(params); }}
                className="text-slate-400 hover:text-slate-900 transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={submit} className="p-4 space-y-3" data-testid="new-project-form">
              {[
                { key: "name", label: t("proj.name"), required: true, testid: "proj-name-input" },
                { key: "client", label: t("proj.client"), testid: "proj-client-input" },
                { key: "location", label: t("proj.location"), testid: "proj-location-input" },
              ].map((f) => (
                <div key={f.key}>
                  <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1.5">{f.label}</label>
                  <input
                    data-testid={f.testid}
                    required={f.required}
                    value={form[f.key] || ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#003366]/30 focus:border-[#003366] font-mono"
                  />
                </div>
              ))}
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1.5">{t("proj.description")}</label>
                <textarea
                  data-testid="proj-description-input"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#003366]/30 focus:border-[#003366]"
                />
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                <button
                  type="button"
                  data-testid="cancel-new-project-btn"
                  onClick={() => { setShowNew(false); params.delete("new"); setParams(params); }}
                  className="bg-white text-slate-900 border border-slate-300 hover:bg-slate-50 rounded-xl px-4 py-2 font-medium text-sm transition-colors"
                >
                  {t("proj.cancel")}
                </button>
                <button
                  type="submit"
                  data-testid="submit-new-project-btn"
                  disabled={submitting}
                  className="bg-[#003366] text-white hover:bg-[#002244] rounded-xl px-4 py-2 font-medium text-sm transition-colors disabled:opacity-60 flex items-center gap-2"
                >
                  {submitting && <Loader2 size={14} className="animate-spin" />}
                  {t("proj.create")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
